import type { CheckoutServiceParticipant } from '../../../domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Menu } from '@sunwebgroup/uds-ui/menu'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { ToggleButton, ToggleButtonGroup } from '@sunwebgroup/uds-ui/toggle-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { saveEditingSkiAncillaryServiceValue, updateEditingSkiAncillaryServiceDuration, updateEditingSkiAncillaryServiceLevel, updateEditingSkiAncillaryServiceSportType, updateEditingSkiAncillaryServiceStartDate, updateEditingSkiAncillaryServiceValue } from '@/scopes/checkout/domain/actions/services/ski'
import { createSkiServiceEvent } from '@/scopes/checkout/domain/analytics'
import { useClickFilterSkiAncillaryService } from '@/scopes/checkout/domain/analytics/useClickFilterSkiAncillaryService'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { NoSportType } from '@/scopes/checkout/infrastructure/api/service-mappers/skiAncillaryServiceMapper'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import genericStyles from '../GenericService/GenericService.module.css'
import { getPriceForParticipant } from '../priceHelpers'
import { getDaysRangeFormatted, getPriceDifferenceAndPriceSymbol, shouldShowSignSelectedOption } from './helpers'
import { skiAncillaryDaysRangeByDurationSelector, skiAncillaryLevelSelector, skiAncillaryServiceDurationsSelector, skiAncillaryServiceEditingValueByIdSelector, skiAncillaryServiceHasIncludedOptionSelector, skiAncillaryServiceOptionsListSelector, skiAncillarySportTypeSelector } from './selectors'
import styles from './SkiAncillaryComponents.module.css'
import {
  skiServiceIsBeingEdited,
  useSkiAncillaryServiceStore,
} from './store'

export function SkiLessonService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.SkiLesson)
  useSendAncillaryAvailable(service, 'selectSkiPass')

  if (!service)
    return null

  const { rooms, participants, options } = service

  if (isEmpty(participants))
    return null
  if (isEmpty(options))
    return null

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_description' })}
      glyph={getIconForService(ServiceType.SkiLesson)}
    >
      <div className={styles.checkoutSectionContent}>
        {rooms?.map(({ participantIds }) => {
          return participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <SkiLessonServicePerParticipant
                {...participant}
                key={participantId}
              />
            )
          })
        })}
        {isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <SkiLessonServicePerParticipant {...participant} key={participant.id} />
        ))}
        <ParticipantMenuSelection />
      </div>
    </CheckoutSection>
  )
}

function ParticipantMenuSelection() {
  const { service } = useService(ServiceType.SkiLesson)
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = React.useState(false)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const handleClick = React.useCallback((participantId: string) => {
    if (!participantId)
      return
    if (!service)
      return
    const { participants } = service

    startEditingService({
      participantId,
      serviceType: ServiceType.SkiLesson,
      isEditing: true,
    })

    if (isParticipantMenuOpen) {
      setIsParticipantMenuOpen(false)
    }

    sendEvent(
      createSkiServiceEvent({
        result: 'Add lesson for',
        type: participants[participantId].name,
        description: 'Ski Lesson',
        element: 'clickable text',
      }),
    )
  }, [isParticipantMenuOpen, startEditingService, service, sendEvent])

  if (!service)
    return null

  const { participants } = service

  const participantsWithoutSkiAncillarySelected = Object.values(participants).filter(({ selectedValue }) => !selectedValue)

  if (!participantsWithoutSkiAncillarySelected.length)
    return null

  function toggle() {
    setIsParticipantMenuOpen(!isParticipantMenuOpen)
  }

  return (
    <ListItem variant="outlined" className={styles.participantMenuSelectionListItem}>
      {participantsWithoutSkiAncillarySelected.length > 1 && (
        <Menu.Root open={isParticipantMenuOpen} onOpenChange={toggle}>
          <Menu.Trigger>
            <Button size="S" variant="secondary">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_add_button_for' })}</Button>
          </Menu.Trigger>
          <Menu.Content>
            {participantsWithoutSkiAncillarySelected.map(participant => (
              <Menu.Item key={participant.id} onClick={() => handleClick(participant.id)}>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_participant_menu_item' }, { name: participant.name })}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Root>
      )}
      {participantsWithoutSkiAncillarySelected.length === 1 && (
        <Button size="S" variant="secondary" onClick={() => handleClick(participantsWithoutSkiAncillarySelected[0].id)}>
          {values(participants).length > 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_add_button_per_participant' }, { name: participantsWithoutSkiAncillarySelected[0].name })}
          {values(participants).length === 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_add_button' })}
        </Button>
      )}
    </ListItem>
  )
}

function SkiLessonServicePerParticipant(participant: CheckoutServiceParticipant) {
  const { selectedValue, name, id } = participant
  const { service } = useService(ServiceType.SkiLesson)
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const { isDesktopSm } = useBreakpoints()

  const intl = useIntl()

  const handleClick = React.useCallback(() => {
    startEditingService({
      participantId: id,
      serviceType: ServiceType.SkiLesson,
      isEditing: true,
    })

    sendEvent(
      createSkiServiceEvent({
        result: 'Change',
        type: name,
        description: 'Ski Lesson',
        element: 'element clicked',
      }),
    )
  }, [sendEvent, startEditingService, id, name])

  if (!service)
    return null

  const { options, participants } = service
  const { startDay, endDay } = selectedValue ? getDaysRangeFormatted(options[selectedValue].startDate, options[selectedValue].endDate, intl.locale) : { startDay: '', endDay: '' }

  const selectedOption = selectedValue ? options[selectedValue] : undefined
  const participantPrice = getPriceForParticipant(selectedOption, id)
  const hasIncludedOption = skiAncillaryServiceHasIncludedOptionSelector(service)
  const isIncluded = selectedOption?.isIncluded ?? false

  return (
    <>
      { selectedValue
      && (
        <ListItem variant="outlined" className={styles.participantRowListItem} onClick={handleClick}>
          <TitleListCell
            overline={Object.values(participants).length > 1 ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_for_participant' }, { name }) : undefined}
            title={selectedValue ? options[selectedValue].label : intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_no_ski_lesson_text' })}
            subtitle={(selectedValue && options[selectedValue].duration > '0')
              ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_selected_value_duration' }, { duration: options[selectedValue].duration, daysRange: intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_days_range' }, { startDay, endDay }) })
              : ''}
          />
          <div className={styles.itemPrice}>
            <div className={styles.priceBlock}>
              <Typography as="span" className={styles.price}>
                {isIncluded
                  ? intl.formatMessage({ id: isDesktopSm ? 'checkout_steps_ancillaries_ski_ancillaries_included' : 'checkout_steps_ancillaries_ski_ancillaries_included_truncated' })
                  : intl.formatCurrency(participantPrice, { showSign: shouldShowSignSelectedOption(participantPrice, hasIncludedOption) })}
              </Typography>
              { !isDesktopSm && (
                <Button variant="transparentNeutral" className={styles.openSheetButton}>
                  <Button.Icon glyph="ChevronRight" size={24} />
                </Button>
              )}
              { isDesktopSm && (
                <Button variant="secondary" onClick={handleClick} size="S">
                  {intl.formatMessage({ id: isIncluded ? 'checkout_steps_ancillaries_ski_ancillaries_upgrade_button' : 'checkout_steps_ancillaries_ski_lesson_edit_button' })}
                </Button>
              )}
            </div>
          </div>
        </ListItem>
      )}
      <SkiLessonServiceSheet participant={participant} />
    </>
  )
}

function SkiLessonServiceSheet({ participant }: { participant: CheckoutServiceParticipant }) {
  const { isTablet } = useBreakpoints()
  const skiLessonWrapperSide = isTablet ? 'right' : 'bottom' as const
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { endEditingService } = useSkiAncillaryServiceStore()

  const { id: participantId, name } = participant
  const { service, setServiceValue } = useService(ServiceType.SkiLesson)

  const handleSaveValue = React.useCallback(() => {
    if (!service)
      return
    const { id } = service.editingSelectedValues[participantId]
    endEditingService(`${ServiceType.SkiLesson}-${participantId}`)
    setServiceValue(saveEditingSkiAncillaryServiceValue({ participantId, value: id })(service))
    sendEvent(
      createSkiServiceEvent({
        result: `Save changes`,
        description: 'Ski lesson',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, participantId, service, endEditingService, setServiceValue])

  const handleCancelClick = React.useCallback(() => {
    if (!participantId)
      return
    if (!service)
      return

    endEditingService(`${ServiceType.SkiLesson}-${participantId}`)

    sendEvent(
      createSkiServiceEvent({
        result: `Cancel`,
        description: 'Ski lesson',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, service, endEditingService, participantId])

  const isParticipantBeingEdited = useSkiAncillaryServiceStore((skiServiceIsBeingEdited(`${ServiceType.SkiLesson}-${participantId}`)))

  if (!service)
    return null
  const { participants } = service

  return (
    <Sheet.Root open={isParticipantBeingEdited} onOpenChange={open => !open && handleCancelClick()}>
      <Sheet.Body side={skiLessonWrapperSide}>
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_cancel_text_button' })}
          titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_title' })}
        />
        <Sheet.Content className={styles.skiAncillaryContent}>
          <div className={styles.skiAncillaryLabel}>
            {participants && Object.values(participants).length > 1 && <Typography variant="body-l-bold-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_for_participant_label' }, { name })}</Typography>}
            <Typography variant="body-m-regular-snug">
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_description' })}
            </Typography>
          </div>
          <SportTypeFilter participantId={participantId} />
          <DurationFilter participantId={participantId} />
          <RangeDateFilter participantId={participantId} />
          <LevelFilter participantId={participantId} />
          <SelectOptionFilter participantId={participantId} />
        </Sheet.Content>
        <Sheet.Footer>
          <Button variant="primary" onClick={handleSaveValue} fullWidth>
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_accept_text_button' })}
          </Button>
        </Sheet.Footer>
      </Sheet.Body>
    </Sheet.Root>
  )
}

function SportTypeFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillarySportTypeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleSportTypeChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceSportType({
        participantId,
        value,
      }),
    )
    sendSkiAncillarySportTypeFilterClickEvent(value)
  }, [sendSkiAncillarySportTypeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  const sportTypes = skiAncillarySportTypeSelector(service)

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_sport_type_text' })}>
      <ToggleButtonGroup className={styles.styledToggleGroup} type="single" defaultValue={sportTypeSelected ?? ''} onValueChange={handleSportTypeChange}>
        {sportTypes.map(sportType => (
          <ToggleButton key={sportType} value={sportType} disabled={sportType === sportTypeSelected}>
            <Typography>
              {sportType !== NoSportType && sportType}
              {sportType === NoSportType && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_no_sport_type_text' })}
            </Typography>
          </ToggleButton>
        ))}
        { !sportTypes.includes(NoSportType) && (
          <ToggleButton value={NoSportType} disabled={NoSportType === sportTypeSelected}><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_no_sport_type_text' })}</Typography></ToggleButton>
        )}
      </ToggleButtonGroup>
    </FormField>
  )
}

function DurationFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryDurationFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleDurationChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceDuration({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryDurationFilterClickEvent(value)
  }, [sendSkiAncillaryDurationFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const durations = skiAncillaryServiceDurationsSelector(service)

  const { duration: durationSelected, sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  if (sportTypeSelected === NoSportType)
    return null
  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_duration_text' })}>
      <ToggleButtonGroup className={styles.styledToggleGroup} type="single" defaultValue={durationSelected ?? ''} onValueChange={handleDurationChange}>
        {durations.map(({ value }) => (
          <ToggleButton key={value} value={value} disabled={value === durationSelected}>
            <Typography>{value}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormField>
  )
}

function RangeDateFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryRangeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleStartDateChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceStartDate({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryRangeFilterClickEvent(participantId, value)
  }, [sendSkiAncillaryRangeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { duration: durationSelected, sportType: sportTypeSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const availableDaysRange = skiAncillaryDaysRangeByDurationSelector(service, durationSelected ?? '', sportTypeSelected ?? '')

  if (sportTypeSelected === NoSportType)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_start_date_text' })}>
      <SelectableRadioButtonGroup value={startDateSelected} onValueChange={handleStartDateChange}>
        <div className={genericStyles.optionItemsContainer}>
          {availableDaysRange.map((range, index) => {
            const { start, end } = range
            const { startDay, endDay } = getDaysRangeFormatted(start, end, intl.locale)

            return (
              <SelectableListItem value={start} key={index} className={genericStyles.itemOptionWrapper}>
                <div className={genericStyles.itemOption}>
                  <div className={genericStyles.itemOptionControl}>
                    <RadioButton value={start} />
                  </div>
                  <div className={genericStyles.itemOptionContent}>
                    <Typography variant="body-m-regular-tight">
                      {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_days_range' }, { startDay, endDay })}
                    </Typography>
                  </div>
                </div>
              </SelectableListItem>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}

function LevelFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryLevelFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson')

  const handleLevelChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceLevel({
        participantId,
        value,
      }),
    )
    sendSkiAncillaryLevelFilterClickEvent(value)
  }, [sendSkiAncillaryLevelFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { level: levelSelected, sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  const levels = skiAncillaryLevelSelector(service, sportTypeSelected ?? '', participantId)
  const defaultLevel = levels[0]

  if (sportTypeSelected === NoSportType)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_levels_text' })}>
      <ToggleButtonGroup className={styles.styledToggleGroup} type="single" value={levelSelected ?? defaultLevel} onValueChange={handleLevelChange}>
        {levels.map(level => (
          <ToggleButton key={level} value={level} disabled={level === levelSelected}>
            <Typography>{intl.formatMessage({ id: `checkout_steps_ancillaries_ski_lesson_sheet_levels_${level?.toLowerCase()}` })}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {levelSelected && sportTypeSelected && (
        <Typography as="span" variant="body-m-regular-snug" className={styles.sheetLevelsHint}>
          {intl.formatMessage({ id: `checkout_steps_ancillaries_ski_lesson_sheet_levels_hint_${sportTypeSelected.toLowerCase()}_${levelSelected.toLowerCase()}` })}
        </Typography>
      ) }
    </FormField>
  )
}

function SelectOptionFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()

  const { service, setServiceLocalValue } = useService(ServiceType.SkiLesson)
  const { sendSkiAncillaryOptionFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski lesson', 'Select package')

  const handleValueChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceValue({
        participantId,
        value,
      }),
    )
    sendSkiAncillaryOptionFilterClickEvent(value)
  }, [sendSkiAncillaryOptionFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { options, participants } = service
  const { duration: durationSelected, id: optionSelectedId, level: levelSelected, sportType: sportTypeSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const { selectedValue, availableOptions: availableOptionsParticipant } = participants[participantId]

  const levels = skiAncillaryLevelSelector(service, sportTypeSelected ?? '', participantId)
  const defaultLevel = levels[0]

  const availableOptionsForSelectedDuration = skiAncillaryServiceOptionsListSelector(service, durationSelected ?? '', startDateSelected, sportTypeSelected ?? '', levelSelected ?? defaultLevel)

  if (sportTypeSelected === NoSportType)
    return null
  if (!availableOptionsForSelectedDuration.length)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_lesson_sheet_options_text' })}>
      <SelectableRadioButtonGroup value={optionSelectedId ?? undefined} onValueChange={handleValueChange}>
        <div className={genericStyles.optionItemsContainer}>
          {availableOptionsForSelectedDuration
            .filter(optionId => availableOptionsParticipant.includes(optionId))
            .map((optionId) => {
              const { id, label, badge, isIncluded } = options[optionId]
              const participantPrice = getPriceForParticipant(options[optionId], participantId)
              const { priceDifference } = getPriceDifferenceAndPriceSymbol(selectedValue, participantPrice, options, participantId)

              return (
                <SelectableListItem value={id} key={optionId} className={genericStyles.itemOptionWrapper}>
                  <div className={genericStyles.itemOption}>
                    <div className={genericStyles.itemOptionControl}>
                      <RadioButton value={id} />
                    </div>
                    <div className={genericStyles.itemOptionContent}>
                      {badge && <Tag variant="success-tinted" size="M" className={cn(genericStyles.itemOptionTag)}>{badge}</Tag>}
                      <Typography variant="body-m-regular-tight">{label}</Typography>
                    </div>
                    <div className={genericStyles.itemOptionTrailing}>
                      <Typography as="span" className={styles.price}>
                        {isIncluded ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_included' }) : intl.formatCurrency(priceDifference, { showSign: priceDifference !== 0 })}
                      </Typography>
                    </div>
                  </div>
                </SelectableListItem>
              )
            },
            )}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}
